// frontend/src/components/ProductsTab.js
import { useState } from "react";
import RichTextEditor from "@/components/RichTextEditor";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { SortableItem } from "@/components/SortableItem";
import { ACCENT_OPTIONS, PALETTE_OPTIONS, Field, TextInput } from "@/components/shared";
import { newProductDefaults, validateProduct } from "@/lib/catalogRules";
import { mergedFormats } from "@/lib/siteConfigStore";

function ProductEditor({ product, orgConfig, siteConfig, allowedKinds, onUpdate, onDelete }) {
  const stores = orgConfig?.stores || [];
  const formats = mergedFormats(orgConfig, siteConfig);
  const matchingStores = stores.filter((s) => s.kind === product.kind);

  const update = (patch) => onUpdate({ ...product, ...patch });

  const handleKindChange = (kind) => {
    const patch = { kind };
    const currentStore = stores.find((s) => s.id === product.storeId);
    if (currentStore && currentStore.kind !== kind) {
      patch.storeId = "";
    }
    update(patch);
  };

  const handleCheckoutUrlChange = (url) => update({ checkoutUrl: url, buttonUrl: url });

  const store = product.storeId ? stores.find((s) => s.id === product.storeId) : null;
  const errors = validateProduct({ product, store, allowedKinds });

  return (
    <div className="leadline-border-thin bg-white p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Name">
          <TextInput value={product.name} onChange={(v) => update({ name: v })} />
        </Field>
        <Field label="Accent Color">
          <select value={product.accent || "amber"} onChange={(e) => update({ accent: e.target.value })} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm">
            {ACCENT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Description">
        <RichTextEditor value={product.description || ""} onChange={(v) => update({ description: v })} minHeight="3rem" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Kind">
          <select value={product.kind || allowedKinds[0]} onChange={(e) => handleKindChange(e.target.value)} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm">
            {allowedKinds.map((kind) => (
              <option key={kind} value={kind}>{kind}</option>
            ))}
          </select>
        </Field>
        <Field label="Format">
          <select value={product.format || ""} onChange={(e) => update({ format: e.target.value })} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm">
            <option value="">—</option>
            {formats.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Store">
          <select value={product.storeId || ""} onChange={(e) => update({ storeId: e.target.value })} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm">
            <option value="">None (free / no checkout)</option>
            {matchingStores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Checkout URL">
          <TextInput value={product.checkoutUrl || product.buttonUrl || ""} onChange={handleCheckoutUrlChange} placeholder="https://..." />
        </Field>
      </div>
      <Field label="Button Label (optional)">
        <TextInput value={product.buttonLabel} onChange={(v) => update({ buttonLabel: v })} placeholder="e.g., Get it on LemonSqueezy" />
      </Field>
      {errors.length > 0 && (
        <div className="space-y-0.5">
          {errors.map((err) => (
            <p key={err} className="text-xs text-red-600">{err}</p>
          ))}
        </div>
      )}
      <button onClick={onDelete} className="text-xs text-red-600 hover:underline mt-1">Delete product</button>
    </div>
  );
}

// Single group editor with collapsible product list
function GroupEditor({ group, products, colors, orgConfig, siteConfig, allowedKinds, onUpdateGroup, onDeleteGroup, onUpdateProduct, onDeleteProduct, onAddProduct, onReorderProducts }) {
  const [expanded, setExpanded] = useState(true);
  const groupProducts = products.filter((p) => p.group === group.id).sort((a, b) => a.order - b.order);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleProductDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = groupProducts.map((p) => p.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(groupProducts, oldIndex, newIndex).map((p, i) => ({ ...p, order: i + 1 }));
    onReorderProducts(group.id, reordered);
  };

  const headingColor = colors[group.colorKey] || "#ccc";

  return (
    <div className="leadline-border overflow-hidden bg-[#F5EDE0]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 text-sm font-medium text-left flex items-center justify-between text-white"
        style={{ backgroundColor: headingColor }}
      >
        <span>{group.title || "Untitled Group"} ({groupProducts.length} products)</span>
        <span>{expanded ? "▾" : "▸"}</span>
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Group Title">
              <TextInput value={group.title} onChange={(v) => onUpdateGroup({ ...group, title: v })} />
            </Field>
            <Field label="Color">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 leadline-border-thin" style={{ backgroundColor: headingColor }} />
                <select
                  value={group.colorKey || "primary"}
                  onChange={(e) => onUpdateGroup({ ...group, colorKey: e.target.value })}
                  className="leadline-border-thin px-2 py-1 bg-white text-sm flex-1"
                >
                  {PALETTE_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </Field>
          </div>
          <Field label="Group Intro (optional)">
            <RichTextEditor value={group.intro || ""} onChange={(v) => onUpdateGroup({ ...group, intro: v })} minHeight="2.5rem" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#3A3530] cursor-pointer">
            <input type="checkbox" checked={group.textOnDark || false} onChange={(e) => onUpdateGroup({ ...group, textOnDark: e.target.checked })} className="accent-[#2B7D6E]" />
            Light text on heading (for dark backgrounds)
          </label>

          <div className="border-t border-[#3A3530] border-opacity-20 pt-3 mt-3">
            <p className="text-xs font-medium text-[#3A3530] mb-2">Products</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProductDragEnd}>
              <SortableContext items={groupProducts.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {groupProducts.map((product) => (
                    <SortableItem key={product.id} id={product.id}>
                      <ProductEditor
                        product={product}
                        orgConfig={orgConfig}
                        siteConfig={siteConfig}
                        allowedKinds={allowedKinds}
                        onUpdate={onUpdateProduct}
                        onDelete={() => onDeleteProduct(product.id)}
                      />
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <button
              onClick={() => onAddProduct(group.id)}
              className="leadline-border-thin px-3 py-1 text-xs bg-white text-[#3A3530] mt-2 hover:bg-[#F5EDE0]"
            >
              + Add Product
            </button>
          </div>

          <button onClick={onDeleteGroup} className="text-xs text-red-600 hover:underline">
            {groupProducts.length > 0 ? `Delete group (will remove ${groupProducts.length} products)` : "Delete group"}
          </button>
        </div>
      )}
    </div>
  );
}

function ProductsTab({ config, onChange, orgConfig, allowedKinds }) {
  const groups = [...(config.productGroups || [])].sort((a, b) => a.order - b.order);
  const products = config.products || [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const deepClone = () => JSON.parse(JSON.stringify(config));

  const handleGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = groups.map((g) => g.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = deepClone();
    next.productGroups = arrayMove([...next.productGroups].sort((a, b) => a.order - b.order), oldIndex, newIndex).map((g, i) => ({ ...g, order: i }));
    onChange(next);
  };

  const updateGroup = (updated) => {
    const next = deepClone();
    next.productGroups = next.productGroups.map((g) => g.id === updated.id ? updated : g);
    onChange(next);
  };

  const addGroup = () => {
    const next = deepClone();
    const id = `group-${Date.now()}`;
    const maxOrder = next.productGroups.reduce((max, g) => Math.max(max, g.order), -1);
    next.productGroups.push({ id, title: "New Group", intro: "", colorKey: "primary", textOnDark: false, order: maxOrder + 1 });
    onChange(next);
  };

  const deleteGroup = (groupId) => {
    if (!window.confirm("Delete this group and all its products?")) return;
    const next = deepClone();
    next.productGroups = next.productGroups.filter((g) => g.id !== groupId);
    next.products = next.products.filter((p) => p.group !== groupId);
    onChange(next);
  };

  const updateProduct = (updated) => {
    const next = deepClone();
    next.products = next.products.map((p) => p.id === updated.id ? updated : p);
    onChange(next);
  };

  const addProduct = (groupId) => {
    const next = deepClone();
    const groupProducts = next.products.filter((p) => p.group === groupId);
    const id = `product-${Date.now()}`;
    next.products.push({
      id,
      group: groupId,
      order: groupProducts.reduce((max, p) => Math.max(max, p.order), 0) + 1,
      ...newProductDefaults(allowedKinds),
    });
    onChange(next);
  };

  const deleteProduct = (productId) => {
    if (!window.confirm("Delete this product?")) return;
    const next = deepClone();
    next.products = next.products.filter((p) => p.id !== productId);
    onChange(next);
  };

  const reorderProducts = (groupId, reorderedGroupProducts) => {
    const next = deepClone();
    const otherProducts = next.products.filter((p) => p.group !== groupId);
    next.products = [...otherProducts, ...reorderedGroupProducts];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
        <SortableContext items={groups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
          {groups.map((group) => (
            <SortableItem key={group.id} id={group.id}>
              <GroupEditor
                group={group}
                products={products}
                colors={config.colors}
                orgConfig={orgConfig}
                siteConfig={config}
                allowedKinds={allowedKinds}
                onUpdateGroup={updateGroup}
                onDeleteGroup={() => deleteGroup(group.id)}
                onUpdateProduct={updateProduct}
                onDeleteProduct={deleteProduct}
                onAddProduct={addProduct}
                onReorderProducts={reorderProducts}
              />
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>

      <button
        onClick={addGroup}
        className="leadline-border-thin px-3 py-1 text-xs bg-[#1E1E1E] text-[#F5EDE0] hover:opacity-80"
      >
        + Add Group
      </button>
    </div>
  );
}

export default ProductsTab;
